import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  TextField
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Download,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  InsertDriveFile,
  Check
} from '@mui/icons-material';
import clientFilesService, { ClientFile, UploadProgress, FILE_CATEGORIES, FileCategory } from '../services/clientFilesService';
import blobService from '../services/storage/blobService';
import { useToast } from '../context/ToastContext';
import { useWorkflowContext } from '../context/WorkflowContext';
import { snowflakeApi } from '../utils/api';

interface ClientFileUploadProps {
  clientId: string;
  clientName: string;
  workflowId?: string;
  onDeleteFile?: (file: ClientFile) => void;
  onAddFile?: (file: ClientFile) => void;
  onStatusChange?: (files: ClientFile[]) => void;
  autoRefresh?: boolean; // Enable automatic polling for file status updates
  refreshInterval?: number; // Polling interval in milliseconds (default: 5000)
}

interface FileUploadState {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
  category: FileCategory;
}

interface CSVValidationResult {
  isValid: boolean;
  validationMessage: string;
  missingColumns?: string[];
  extraColumns?: string[];
  aiFeedback?: string;
}

const StyledDropZone = styled(Paper)(({ theme }) => ({
  border: '2px dashed',
  borderColor: alpha(theme.palette.primary.main, 0.3),
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2.5),
  textAlign: 'center',
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&.dragging': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  height: '32px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
  '&.active': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    }
  },
  '&.inactive': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    }
  }
}));

const ClientFileUpload: React.FC<ClientFileUploadProps> = ({
  clientId,
  clientName,
  workflowId: propWorkflowId,
  onDeleteFile,
  onAddFile,
  onStatusChange,
  autoRefresh = false,
  refreshInterval = 5000
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const { workflowId } = useWorkflowContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropAreaFileInputRef = useRef<HTMLInputElement>(null);
  
  const [existingFiles, setExistingFiles] = useState<ClientFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, FileUploadState>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPolling, setIsPolling] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<ClientFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<FileCategory>('Google Search Console');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [validationResult, setValidationResult] = useState<CSVValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Use workflowId from props first, fallback to context
  const currentWorkflowId = propWorkflowId || workflowId;

  // Prevent browser close/refresh when files are uploading
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploadingFiles.size > 0) {
        e.preventDefault();
        // Modern browsers ignore custom messages and show their own standard warning
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadingFiles]);

  // Load existing files when component mounts
  useEffect(() => {
    loadClientFiles();
  }, [clientId, currentWorkflowId]);

  // Auto-refresh polling for file status updates
  useEffect(() => {
    if (!autoRefresh || !clientId || isInitialLoad || !isPolling) return;

    const interval = setInterval(() => {
      // Poll without showing loading spinner
      loadClientFiles(false);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, clientId, currentWorkflowId, isInitialLoad, isPolling]);

  // Notify parent when files change
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(existingFiles);
    }
  }, [existingFiles, onStatusChange]);

  const loadClientFiles = async (showLoadingSpinner: boolean = true) => {
    try {
      // Only show loading spinner on initial load, not during polling
      if (showLoadingSpinner) {
        setLoading(true);
      }
      
      const files = currentWorkflowId 
        ? await clientFilesService.getClientWorkflowFiles(clientId, currentWorkflowId)
        : await clientFilesService.getClientFiles(clientId);
      
      setExistingFiles(files);
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
      
      // Check if all files are completed - stop polling if so
      if (autoRefresh && files.length > 0) {
        const allCompleted = files.every(file => 
          file.load_status === 'LOAD COMPLETED' || file.load_status === 'To Delete'
        );
        
        if (allCompleted) {
          setIsPolling(false);
        } else {
          // Restart polling if there are incomplete files
          setIsPolling(true);
        }
      }
    } catch (error) {
      console.error('Error loading client files:', error);
      if (showLoadingSpinner) {
        showToast('Failed to load existing files', 'error');
      }
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  // Get category status - returns true if there's an active file for the category
  const getCategoryStatus = (category: FileCategory): boolean => {
    return existingFiles.some(file => 
      file.file_category === category
    );
  };

  // Handle category chip click
  const handleCategoryChipClick = (category: FileCategory) => {
    setSelectedCategory(category);
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleDropAreaClick = useCallback(() => {
    if (dropAreaFileInputRef.current) {
      dropAreaFileInputRef.current.click();
    }
  }, []);

  const handleDropAreaFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      // Filter for CSV and TXT files only
      const validFiles = files.filter(file => {
        const extension = file.name.toLowerCase().split('.').pop();
        return extension === 'csv' || extension === 'txt';
      });

      if (validFiles.length === 0) {
        showToast('Only CSV and TXT files are allowed', 'error');
        return;
      }

      if (validFiles.length !== files.length) {
        showToast(`${files.length - validFiles.length} files were skipped (only CSV and TXT allowed)`, 'warning');
      }

      // Store pending files and show category selection dialog
      setPendingFiles(validFiles);
      setShowCategoryDialog(true);
      
      // Reset input value
      e.target.value = '';
    }
  }, [showToast]);

  const handleFiles = useCallback((files: File[]) => {
    // Filter for CSV and TXT files only
    const validFiles = files.filter(file => {
      const extension = file.name.toLowerCase().split('.').pop();
      return extension === 'csv' || extension === 'txt';
    });

    if (validFiles.length === 0) {
      showToast('Only CSV and TXT files are allowed', 'error');
      return;
    }

    if (validFiles.length !== files.length) {
      showToast(`${files.length - validFiles.length} files were skipped (only CSV and TXT allowed)`, 'warning');
    }

    // Store pending files and show category selection dialog
    setPendingFiles(validFiles);
    setShowCategoryDialog(true);
  }, [showToast]);

  const uploadFile = useCallback(async (file: File, category: FileCategory, dateFromValue?: string, dateToValue?: string) => {
    const fileId = `${file.name}_${Date.now()}`;
    
    // Initialize upload state
    setUploadingFiles(prev => new Map(prev.set(fileId, {
      file,
      progress: 0,
      status: 'uploading',
      category
    })));

    try {

      const uploadedFile = await clientFilesService.uploadClientFile(
        file,
        clientId,
        clientName,
        category,
        (progress: UploadProgress) => {
          setUploadingFiles(prev => {
            const current = prev.get(fileId);
            if (current) {
              return new Map(prev.set(fileId, {
                ...current,
                progress: progress.percentComplete
              }));
            }
            return prev;
          });
        },
        dateFromValue,
        dateToValue,
        currentWorkflowId // Use currentWorkflowId which prioritizes props over context
      );

      // Remove any existing files for this category and add the new uploaded file
      setExistingFiles(prev => {
        // Filter out any existing files for the same category
        const filteredFiles = prev.filter(file => 
          file.file_category !== category
        );
        // Add the new uploaded file at the beginning
        return [uploadedFile, ...filteredFiles];
      });

      // Remove from uploading files immediately since upload is complete
      setUploadingFiles(prev => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });

      // Notify parent component of new file
      if (onAddFile) {
        onAddFile(uploadedFile);
      }
      
      // Restart polling when new file is added
      if (autoRefresh) {
        setIsPolling(true);
      }

      showToast(`${file.name} uploaded successfully`, 'success');

    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Mark as error
      setUploadingFiles(prev => {
        const current = prev.get(fileId);
        if (current) {
          return new Map(prev.set(fileId, {
            ...current,
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed'
          }));
        }
        return prev;
      });

      showToast(`Failed to upload ${file.name}`, 'error');
    }
  }, [clientId, clientName, showToast, currentWorkflowId]);

  const handleFilesWithPresetCategory = useCallback((files: File[], category: FileCategory) => {
    // Filter for CSV and TXT files only
    const validFiles = files.filter(file => {
      const extension = file.name.toLowerCase().split('.').pop();
      return extension === 'csv' || extension === 'txt';
    });

    if (validFiles.length === 0) {
      showToast('Only CSV and TXT files are allowed', 'error');
      return;
    }

    if (validFiles.length !== files.length) {
      showToast(`${files.length - validFiles.length} files were skipped (only CSV and TXT allowed)`, 'warning');
    }

    // Show category dialog with selected files and preset category for date range input
    setPendingFiles(validFiles);
    setSelectedCategory(category);
    setShowCategoryDialog(true);
  }, [showToast]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFilesWithPresetCategory(files, selectedCategory);
      // Reset input value
      e.target.value = '';
    }
  }, [selectedCategory, handleFilesWithPresetCategory]);

  const handleDeleteClick = (file: ClientFile) => {
    setFileToDelete(file);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) return;

    setIsDeleting(true);
    try {
      await clientFilesService.deleteClientFile(fileToDelete.id);
      
      // Remove the file from the local state immediately
      setExistingFiles(prev => prev.filter(file => file.id !== fileToDelete.id));
      
      // Notify parent component of file deletion
      if (onDeleteFile) {
        onDeleteFile(fileToDelete);
      }
      
      showToast(`${fileToDelete.filename} deleted successfully`, 'success');
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast(`Failed to delete ${fileToDelete.filename}`, 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleDownloadFile = async (file: ClientFile) => {
    try {
      // Get a fresh download URL using the clientFilesService
      // This handles both full URLs and relative paths
      const downloadUrl = await clientFilesService.getFileDownloadUrl(file.file_path);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast(`Failed to download ${file.filename}`, 'error');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value as FileCategory);
  };

  const handleCategoryConfirm = async () => {
    // Validate date range if both dates are provided
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      showToast('From Date cannot be later than To Date', 'error');
      return;
    }
    
    // Check if we have CSV files to validate
    const csvFiles = pendingFiles.filter(file => 
      file.name.toLowerCase().endsWith('.csv')
    );
    
    // If we have CSV files and haven't validated yet, validate first
    if (csvFiles.length > 0 && !validationResult) {
      setIsValidating(true);
      
      try {
        const fileToValidate = csvFiles[0];
        
        const headers = await readCSVHeaders(fileToValidate);
        const result = await validateCSVHeaders(headers, selectedCategory);
        setValidationResult(result);
        
        // If validation passes, proceed with upload
        if (result.isValid) {
          // Proceed with upload
          pendingFiles.forEach(file => uploadFile(file, selectedCategory, dateFrom || undefined, dateTo || undefined));
          
          // Clear state and close dialog
          setPendingFiles([]);
          setDateFrom('');
          setDateTo('');
          setValidationResult(null);
          setShowCategoryDialog(false);
        }
        // If validation fails, show results and wait for user action
      } catch (error) {
        console.error('Error during validation:', error);
        // Set a failed validation result so user can proceed if they click again
        setValidationResult({
          isValid: false,
          validationMessage: 'Validation service unavailable',
          aiFeedback: 'Could not connect to validation service. Please verify your CSV headers manually.'
        });
      } finally {
        setIsValidating(false);
      }
      return; // Exit here to show validation results
    }
    
    // If we already have validation results (user clicked again after seeing warnings) or no CSV files, proceed with upload
    pendingFiles.forEach(file => uploadFile(file, selectedCategory, dateFrom || undefined, dateTo || undefined));
    
    // Clear pending files, date range, validation result, and close dialog
    setPendingFiles([]);
    setDateFrom('');
    setDateTo('');
    setValidationResult(null);
    setShowCategoryDialog(false);
  };

  const handleCategoryCancel = () => {
    setPendingFiles([]);
    setDateFrom('');
    setDateTo('');
    setValidationResult(null);
    setShowCategoryDialog(false);
  };

  // Read first 6 lines from CSV file for LLM validation
  const readCSVHeaders = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          // Get first 6 lines as-is
          const lines = text.split('\n').slice(0, 6).join('\n');
          resolve(lines);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(reader.error);
      
      // Read only first few KB to get the first lines
      const blob = file.slice(0, 4096);
      reader.readAsText(blob);
    });
  };

  // Validate CSV headers with AI
  const validateCSVHeaders = async (headers: string, category: FileCategory): Promise<CSVValidationResult> => {
    try {
      const response = await snowflakeApi.post('/api/v1/workflow-v2/validate-csv-headers', {
        csv_headers: headers,
        file_category: category,
        client_id: clientId,
        workflow_id: currentWorkflowId
      });
      
      return {
        isValid: response.data.is_valid,
        validationMessage: response.data.validation_message,
        missingColumns: response.data.missing_columns,
        extraColumns: response.data.extra_columns,
        aiFeedback: response.data.ai_feedback
      };
    } catch (error) {
      console.error('Error validating CSV headers:', error);
      throw error;
    }
  };

  return (
    <>
    {/* Category Selection Dialog */}
    <Dialog
      open={showCategoryDialog}
      onClose={handleCategoryCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="span">
          Select File Category
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Please select the category for the file(s) you're uploading:
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="file-category-label">File Category</InputLabel>
          <Select
            labelId="file-category-label"
            value={selectedCategory}
            label="File Category"
            onChange={handleCategoryChange}
          >
            {FILE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Validation Results (shown after validation) */}
        {validationResult && (
          <Box sx={{ mb: 3 }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: validationResult.isValid 
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.warning.main, 0.1),
                borderColor: validationResult.isValid 
                  ? theme.palette.success.main
                  : theme.palette.warning.main
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                {validationResult.isValid ? (
                  <CheckCircle sx={{ color: theme.palette.success.main }} />
                ) : (
                  <ErrorIcon sx={{ color: theme.palette.warning.main }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {validationResult.validationMessage}
                  </Typography>
                  
                  {validationResult.missingColumns && validationResult.missingColumns.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Missing columns:</strong> {validationResult.missingColumns.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  
                  {validationResult.extraColumns && validationResult.extraColumns.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Extra columns:</strong> {validationResult.extraColumns.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  
                  {validationResult.aiFeedback && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>AI Feedback:</strong> {validationResult.aiFeedback}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
        
        {/* Date Range Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Data Range (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Specify the date range that this data covers
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Start date of data range"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="End date of data range"
                inputProps={{
                  min: dateFrom || undefined
                }}
              />
            </Grid>
          </Grid>
        </Box>
        
        {pendingFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Files to upload ({pendingFiles.length}):
            </Typography>
            <Box sx={{ mt: 1 }}>
              {pendingFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleCategoryCancel} 
          variant="outlined"
          disabled={isValidating}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleCategoryConfirm} 
          variant="contained"
          startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
          disabled={pendingFiles.length === 0 || isValidating}
          color={validationResult && !validationResult.isValid ? 'warning' : 'primary'}
        >
          {isValidating 
            ? 'Validating...' 
            : validationResult && !validationResult.isValid 
              ? 'Upload Anyway' 
              : 'Upload Files'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Main Upload Dialog */}
    <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}
    >
      {/* Upload Warning Alert */}
      {uploadingFiles.size > 0 && (
        <Alert 
          severity="warning" 
          icon={<CloudUpload />}
          sx={{ 
            mx: 3, 
            mt: 3,
            fontWeight: 500,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Upload in Progress - Please Don't Close This Browser
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {uploadingFiles.size} file{uploadingFiles.size > 1 ? 's are' : ' is'} currently uploading directly to secure storage. 
              Closing this browser or navigating away will interrupt the upload.
            </Typography>
          </Box>
        </Alert>
      )}

      <Box sx={{ p: 3 }}>
        {/* Category Status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {FILE_CATEGORIES.map((category) => {
              const hasActiveFile = getCategoryStatus(category);
              return (
                <CategoryChip
                  key={category}
                  label={category}
                  size="small"
                  className={hasActiveFile ? 'active' : 'inactive'}
                  icon={hasActiveFile ? <Check /> : undefined}
                  onClick={() => handleCategoryChipClick(category)}
                  variant={hasActiveFile ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>
        </Box>

        {/* Upload Area */}
        <Box sx={{ mb: 3 }}>
          
          <Box>
            {/* Hidden file input for category chips */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".csv,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {/* Hidden file input for drop area */}
            <input
              ref={dropAreaFileInputRef}
              type="file"
              multiple
              accept=".csv,.txt"
              onChange={handleDropAreaFileSelect}
              style={{ display: 'none' }}
            />
            
            <StyledDropZone
              className={isDragging ? 'dragging' : ''}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropAreaClick}
              elevation={0}
            >
              <CloudUpload sx={{ fontSize: 36, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Drop files here or click to select (CSV and TXT files only)
              </Typography>
            </StyledDropZone>
          </Box>
        </Box>

        {/* Uploading Files */}
        {uploadingFiles.size > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Uploading Files
            </Typography>
            {Array.from(uploadingFiles.entries()).map(([fileId, fileState]) => (
              <Paper key={fileId} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <InsertDriveFile sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {fileState.file.name}
                  </Typography>
                  <Chip
                    label={fileState.category}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontSize: '0.75rem'
                    }}
                  />
                  {fileState.status === 'uploading' && (
                    <CircularProgress size={20} />
                  )}
                  {fileState.status === 'success' && (
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  )}
                  {fileState.status === 'error' && (
                    <ErrorIcon sx={{ color: theme.palette.error.main }} />
                  )}
                </Box>
                
                {fileState.status === 'uploading' && (
                  <LinearProgress 
                    variant="determinate" 
                    value={fileState.progress} 
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                )}
                
                {fileState.status === 'error' && fileState.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {fileState.error}
                  </Alert>
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* Existing Files */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Existing Files ({existingFiles.length})
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : existingFiles.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No files uploaded yet. Use the upload area above to add files.
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined">
              <List>
                {existingFiles.map((file, index) => (
                  <ListItem 
                    key={file.id || index}
                    divider={index < existingFiles.length - 1}
                    sx={{ 
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      py: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {file.filename}
                            </Typography>
                            {file.file_category && (
                              <Chip
                                label={file.file_category}
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                            {file.load_status && (
                              <Chip
                                label={file.load_status}
                                size="small"
                                color={
                                  file.load_status === 'LOAD COMPLETED' ? 'success' :
                                  file.load_status === 'LOAD TRIGGERED' || file.load_status === 'triggered' || file.load_status === 'Pending' || file.load_status === 'Processing' ? 'warning' : 
                                  file.load_status === 'To Delete' ? 'error' : 
                                  'default'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {file.file_size && formatFileSize(file.file_size)}
                              {file.created_at && ` • Uploaded ${formatDate(file.created_at)}`}
                            </Typography>
                            {(file.date_from || file.date_to) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Data Range: {file.date_from ? new Date(file.date_from).toLocaleDateString() : 'N/A'} - {file.date_to ? new Date(file.date_to).toLocaleDateString() : 'N/A'}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          edge="end"
                          onClick={() => handleDownloadFile(file)}
                          size="small"
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteClick(file)}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Show linear progress for files being processed (only when autoRefresh is enabled) */}
                    {autoRefresh && file.load_status && file.load_status !== 'LOAD COMPLETED' && file.load_status !== 'To Delete' && file.load_status !== 'LOAD FAILED' && (
                      <LinearProgress 
                        sx={{ 
                          width: '100%',
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.warning.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.warning.main
                          }
                        }} 
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={isDeleting ? undefined : handleDeleteCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="span">
          Confirm Delete
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete "{fileToDelete?.filename}"?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleDeleteCancel} 
          variant="outlined"
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          variant="contained" 
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ClientFileUpload; 